//dependencies
import React, { useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

//components
import Button from "../../../../../components/Button";
import FormsINeedToDoFiltersPanel, { FormsINeedToDoFilters } from "./components/FormsINeedToDoFilterPanel";
import FormsINeedToDoReadableFilters from "./components/FormsINeedToDoReadableFilters";
import FormsINeedToDoTableCols from "./components/FormsINeedToDoTableCols";

//hooks
import { useSearchService } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
import useUserReviewsNavLinksAndPermissions from "../../hooks/useUserReviewsNavLinksAndPermissions";

//services
import { SERVICE_FORMS } from "../../../../../api/services/defs/search-service";
import { ReviewForm } from "../../../../../api/services/searchServiceResultTypes/reviewForm";

//utils
import { getFiltersForSearchService } from "./utils";

//page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { REVIEWS_TABLE_LIMIT, USER_REVIEWS_CONFIG } from "../../constants";

const FormsINeedToDo = () => {
	const { activePage, navLinks, employeeId, userCanCreateReviews, userCanAdd360Forms } =
		useUserReviewsNavLinksAndPermissions();
	const { handleQueryChange } = useSearchQueryParams({ schema: USER_REVIEWS_CONFIG.queryParametersSchema });

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<FormsINeedToDoFilters>({
		defaultSortName: "authors_due_date",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: "any",
		},
	});

	const { data, isLoading } = useSearchService<ReviewForm>({
		service: SERVICE_FORMS,
		filters: getFiltersForSearchService(filters, employeeId, debouncedSearchValue),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Forms",
	});

	//no prefetching on this page since we use the current date time as a query key

	const forms = data?.results || [];
	const totalForms = data?.total || 0;

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const additionalFiltersCount = Object.keys(filters).length;

	const columnDefs = useMemo(() => FormsINeedToDoTableCols(employeeId), [employeeId]);

	const onSortChanged = (event: SortChangedEvent<ReviewForm>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={additionalFiltersCount}
			searchPlaceholder="Search forms..."
		/>
	);

	const drawer = (
		<FormsINeedToDoFiltersPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const toolbarInformationRow = (
		<FormsINeedToDoReadableFilters filters={{ status: "any", ...filters }} setFilters={setFilters} />
	);

	const mainContent = (
		<div>
			<Table<ReviewForm>
				autoHeight
				rowData={forms}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Forms Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{totalForms > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalForms / REVIEWS_TABLE_LIMIT)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const actionButtons = [
		userCanAdd360Forms && (
			<Button data-testid="create360FormButton" onClick={() => handleQueryChange({ modal: "add360Form" })}>
				Add 360 Form
			</Button>
		),
		userCanCreateReviews && (
			<Button
				data-testid="createReviewButton"
				onClick={() => handleQueryChange({ modal: "createReview" })}
				color="green"
				icon={faPlus}
			>
				Create Review
			</Button>
		),
	];

	return (
		<InfoPageTemplate
			employeeID={employeeId}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks}
			activeNavigationLinkKey={activePage}
		/>
	);
};

export default FormsINeedToDo;
